import { useEffect, useRef, useState } from 'react'

import { useI18n } from '../../../../shared/i18n/useI18n.js'

import { HERO_VIEW } from '../../../../shared/constants/heroView.js'

import { MODEL_FRAME_INTERVAL_MS } from '../../constants/animationTimings.js'

import { ButtonHero } from '../ButtonHero/ButtonHero.jsx'

import { ChevronDown } from '../../../../shared/ui/icons/ChevronDown.jsx'
import { RotateLeft } from '../../../../shared/ui/icons/RotateLeft.jsx'
import { RotateRight } from '../../../../shared/ui/icons/RotateRight.jsx'
import { ZoomIn } from '../../../../shared/ui/icons/ZoomIn.jsx'
import { ZoomOut } from '../../../../shared/ui/icons/ZoomOut.jsx'

import styles from './ModelControls.module.scss'

const HOLD_MIN_INTERVAL_MS = MODEL_FRAME_INTERVAL_MS

export function ModelRotateControls({
  isMobile = false,
  isLoading = false,
  loadingProgress = 0,
  activeView,
  activeBuilding,
  activeBuildingName,
  isSingleBuilding,
  onRotateLeft,
  onRotateRight,
  onZoomIn,
  onZoomOut,
}) {
  const { t } = useI18n()
  const [isRotateExpanded, setIsRotateExpanded] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(1)

  const holdRafRef = useRef(null)
  const holdLastRef = useRef(0)

  useEffect(() => () => cancelAnimationFrame(holdRafRef.current), [])

  const startHold = (handler, event) => {
    event?.stopPropagation()
    event?.preventDefault()
    handler()
    holdLastRef.current = performance.now()

    const loop = (now) => {
      if (now - holdLastRef.current >= HOLD_MIN_INTERVAL_MS) {
        handler()
        holdLastRef.current = now
      }
      holdRafRef.current = requestAnimationFrame(loop)
    }

    holdRafRef.current = requestAnimationFrame(loop)
  }

  const stopHold = (event) => {
    event?.stopPropagation()
    event?.preventDefault()
    cancelAnimationFrame(holdRafRef.current)
    holdRafRef.current = null
  }

  const handleZoom = (handler, event) => {
    event.stopPropagation()
    const nextZoom = handler?.()
    if (typeof nextZoom === 'number') setZoomLevel(nextZoom)
  }

  const stopControlPropagation = (event) => event.stopPropagation()

  const renderZoomButton = (direction) => {
    const isIn = direction === 'in'
    const label = t(isIn ? 'common.zoom_in' : 'common.zoom_out')
    const Icon = isIn ? ZoomIn : ZoomOut
    return (
      <ButtonHero
        className={`${styles.heroButton} ${!isMobile ? styles['heroButton--small']: ''}`}
        onClick={(event) => handleZoom(isIn ? onZoomIn : onZoomOut, event)}
        onMouseDown={stopControlPropagation}
        onTouchStart={stopControlPropagation}
        disabled={isLoading || (isIn ? zoomLevel >= 2.5 : zoomLevel <= 1)}
        title={label}
        aria-label={label}
      >
        <Icon className={styles.icon} />
      </ButtonHero>
    )
  }

  const rotateLabel = isLoading
    ? `${loadingProgress}%`
    : (!isSingleBuilding && activeView === HERO_VIEW.BUILDING && (activeBuildingName || activeBuilding))
      ? `${t('common.building')} ${activeBuildingName ?? activeBuilding}`
      : t('hero.rotate_360')

  if (isMobile) {
    return (
      <div
        className={styles.mobileGroup}
        onClick={() => setIsRotateExpanded((prev) => !prev)}
      >
        {isRotateExpanded && (
          <div className={styles.mobileRotatePanel}>
            <div className={styles.zoomButtonsGroup}>
              {renderZoomButton('out')}
              {renderZoomButton('in')}
            </div>
            <div className={styles.buttonsGroupBottomBar}>
              <ButtonHero
                className={styles.heroButton}
                onClick={(event) => event.stopPropagation()}
                onTouchStart={(e) => startHold(onRotateLeft, e)}
                onTouchEnd={stopHold}
                onDragStart={(e) => e.preventDefault()}
                onContextMenu={(event) => event.preventDefault()}
                title={t('hero.rotate_left')}
              >
                <RotateLeft className={styles.icon} />
              </ButtonHero>
              <ButtonHero
                className={styles.heroButton}
                onClick={(event) => event.stopPropagation()}
                onTouchStart={(e) => startHold(onRotateRight, e)}
                onTouchEnd={stopHold}
                onDragStart={(e) => e.preventDefault()}
                onContextMenu={(event) => event.preventDefault()}
                title={t('hero.rotate_right')}
              >
                <RotateRight className={styles.icon} />
              </ButtonHero>
            </div>
          </div>
        )}

        <div 
          className={`${styles.mobileLabel} ${isRotateExpanded ? styles.mobileLabelActive : ''}`} 
          disabled={isLoading}
          style={{
            pointerEvents: isLoading ? 'none' : 'auto',
            color: isLoading ? 'var(--bim-color-text-muted)' : 'inherit',
          }}
        >
          <span>{rotateLabel}</span>
          <ChevronDown
            className={styles.iconSmall}
            style={{
              marginLeft: '0.25rem',
              transform: isRotateExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.3s ease',
              pointerEvents: isLoading ? 'none' : 'auto',
              color: isLoading ? 'var(--bim-color-text-muted)' : 'inherit',
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.rotateGroup}>
      {renderZoomButton('out')}
      <ButtonHero
        className={`${styles.heroButton} ${styles['heroButton--small']}`}
        onMouseDown={(e) => startHold(onRotateLeft, e)}
        onMouseUp={stopHold}
        onMouseLeave={stopHold}
        onTouchStart={(e) => startHold(onRotateLeft, e)}
        onTouchEnd={stopHold}
        onDragStart={(e) => e.preventDefault()}
        title={t('hero.rotate_left')}
      >
        <RotateLeft className={styles.icon} />
      </ButtonHero>

      <div className={styles.rotateLabel}>{rotateLabel}</div>

      <ButtonHero
        className={`${styles.heroButton} ${styles['heroButton--small']}`}
        onMouseDown={(e) => startHold(onRotateRight, e)}
        onMouseUp={stopHold}
        onMouseLeave={stopHold}
        onTouchStart={(e) => startHold(onRotateRight, e)}
        onTouchEnd={stopHold}
        onDragStart={(e) => e.preventDefault()}
        title={t('hero.rotate_right')}
      >
        <RotateRight className={styles.icon} />
      </ButtonHero>
      {renderZoomButton('in')}
    </div>
  )
}
